import { defineFakeRoute } from 'vite-plugin-fake-server/client';
import type { AccountSort } from '@/services/business/accounts';
import { resultError, resultSuccess, routeParam } from '../business/utils';
import {
  abortAccountConnection,
  beginAccountConnection,
  changeAccountEnabled,
  findUpstreamAccount,
  finishAccountConnection,
  readAccountConnection,
  UpstreamAccountError,
  upstreamAccounts,
  upstreamProviders,
} from './accounts-store';

function attempt<T>(operation: () => T) {
  try {
    return resultSuccess(operation());
  } catch (error) {
    if (error instanceof UpstreamAccountError)
      return resultError(error.message, error.status);
    throw error;
  }
}
function objectBody(body: unknown): Record<string, unknown> {
  if (!body || typeof body !== 'object' || Array.isArray(body))
    throw new UpstreamAccountError(422, '请求内容无效');
  return body as Record<string, unknown>;
}
const authRank = { unauthorized: 0, expired: 1, connected: 2 };
export default defineFakeRoute([
  {
    url: '/platform/upstream-accounts',
    method: 'get',
    response: ({ query }) =>
      attempt(() => {
        const page = Number(query.page ?? 1),
          pageSize = Number(query.page_size ?? 10);
        const sort = routeParam(query.sort) as AccountSort | undefined,
          order = routeParam(query.order);
        const provider = routeParam(query.provider),
          auth = routeParam(query.authStatus),
          enabled = routeParam(query.enabled);
        if (
          !Number.isInteger(page) ||
          page < 1 ||
          !Number.isInteger(pageSize) ||
          pageSize < 1 ||
          pageSize > 100 ||
          (sort !== undefined &&
            !['displayName', 'enabled', 'authStatus', 'authorizedAt'].includes(sort)) ||
          (order !== undefined && !['asc', 'desc'].includes(order)) ||
          !!sort !== !!order ||
          (provider !== undefined && !upstreamProviders.some((p) => p === provider)) ||
          (auth !== undefined &&
            !['connected', 'expired', 'unauthorized'].includes(auth)) ||
          (enabled !== undefined && !['enabled', 'disabled'].includes(enabled))
        )
          throw new UpstreamAccountError(422, '查询参数无效');
        const q = (routeParam(query.q) ?? '').trim().toLowerCase();
        const items = upstreamAccounts
          .filter(
            (a) =>
              (!provider || a.provider === provider) &&
              (!auth || a.authStatus === auth) &&
              (!enabled || a.enabled === (enabled === 'enabled')) &&
              (!q ||
                [a.displayName, a.maskedIdentity].some((v) =>
                  v.toLowerCase().includes(q),
                )),
          )
          .sort((a, b) => {
            if (!sort)
              return (
                authRank[a.authStatus] - authRank[b.authStatus] ||
                (b.authorizedAt ?? '').localeCompare(a.authorizedAt ?? '') ||
                a.id.localeCompare(b.id)
              );
            const av =
              sort === 'authStatus'
                ? authRank[a.authStatus]
                : sort === 'enabled'
                  ? Number(a.enabled)
                  : a[sort];
            const bv =
              sort === 'authStatus'
                ? authRank[b.authStatus]
                : sort === 'enabled'
                  ? Number(b.enabled)
                  : b[sort];
            if (av === null || bv === null)
              return av === bv ? a.id.localeCompare(b.id) : av === null ? 1 : -1;
            const difference =
              typeof av === 'number' && typeof bv === 'number'
                ? av - bv
                : String(av).localeCompare(String(bv));
            return difference * (order === 'desc' ? -1 : 1) || a.id.localeCompare(b.id);
          });
        return {
          items: items.slice((page - 1) * pageSize, page * pageSize),
          total: items.length,
          page,
          page_size: pageSize,
        };
      }),
  },
  {
    url: '/platform/upstream-accounts/:id',
    method: 'get',
    response: ({ params }) =>
      attempt(() => findUpstreamAccount(routeParam(params.id) ?? '')),
  },
  {
    url: '/platform/upstream-accounts/:id',
    method: 'patch',
    response: ({ params, body }) =>
      attempt(() =>
        changeAccountEnabled(routeParam(params.id) ?? '', objectBody(body)),
      ),
  },
  {
    url: '/platform/upstream-account-connections',
    method: 'post',
    response: ({ body }) => attempt(() => beginAccountConnection(objectBody(body))),
  },
  {
    url: '/platform/upstream-account-connections/:id',
    method: 'get',
    response: ({ params }) =>
      attempt(() => readAccountConnection(routeParam(params.id) ?? '')),
  },
  {
    url: '/platform/upstream-account-connections/:id/complete',
    method: 'post',
    response: ({ params, body }) =>
      attempt(() =>
        finishAccountConnection(
          routeParam(params.id) ?? '',
          objectBody(body).identityId,
        ),
      ),
  },
  {
    url: '/platform/upstream-account-connections/:id/cancel',
    method: 'post',
    response: ({ params }) =>
      attempt(() => abortAccountConnection(routeParam(params.id) ?? '')),
  },
]);
